<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" :id="offcanvas_id" data-bs-backdrop="static" :aria-labelledby="`${offcanvas_id}_label`">
    <div class="offcanvas-header">
      <button class="btn-back" type="button" name="button" data-test="btnCloseOffcanvas" data-bs-dismiss="offcanvas" aria-label="Close" @click="doCancel">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block" :id="`${offcanvas_id}_label`">
        {{ $t('clinic.clinic') }}
      </h5>
    </div>
    <div class="offcanvas-body scrollbar">
      <vee-form v-if="clinic" v-slot="{ handleSubmit, setFieldValue, setFieldTouched }" as="div">
        <span v-if="captureProviderFunctions(setFieldValue, setFieldTouched)"></span>
        <form id="clinicDataForm" @submit.prevent.stop="handleSubmit(doSave)">
          <section id="general">
            <h3 class="sub-title" ref="targets">{{ $t('createClinic.generalData') }}</h3>
            <div class="form-floating">
              <vee-field name="name" v-model="clinic.name" rules="required" v-slot="{ field, errorMessage }">
                <input v-bind="field" type="text" class="form-control" ref="name" id="name" :placeholder="$t('createClinic.name')" data-test="inputClinicName" autofocus/>
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="name">{{ $t('createClinic.name') }}*</label>
            </div>
            <div class="form-floating">
              <vee-field name="document" v-model="clinic.document" rules="required" v-slot="{ field, errorMessage }">
                <input v-bind="field" type="text" class="form-control" ref="document" id="document" v-maska
                       :data-maska="$t('inputMask.clinicDocument')" :placeholder="$t('createClinic.document')" data-test="inputClinicDocument" />
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="document">{{ $t('createClinic.document') }}*</label>
            </div>
            <div class="form-floating">
              <vee-field name="ownerDocument" v-model="clinic.ownerDocument" v-slot="{ field, errorMessage }">
                <input v-bind="field" type="text" class="form-control" ref="ownerDocument" id="ownerDocument" v-maska
                       :data-maska="$t('inputMask.ownerDocument')" :placeholder="$t('createClinic.ownerDocument')" data-test="inputClinicOwnerDocument" />
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="ownerDocument">{{ $t('createClinic.ownerDocument') }}</label>
            </div>
            <div class="form-floating">
              <vee-field name="email" v-model="clinic.email" rules="required|email" v-slot="{ field, errorMessage }">
                <input v-bind="field" type="text" class="form-control" ref="email" id="email" :placeholder="$t('createClinic.email')" data-test="inputClinicEmail" />
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="email">{{ $t('createClinic.email') }}*</label>
            </div>
            <div class="form-floating">
              <vee-field name="telephone1" v-model="clinic.telephone1" rules="required|min:3" v-slot="{ field, errorMessage }">
                <input v-bind="field" type="text" class="form-control" ref="telephone1" id="telephone1" :placeholder="$t('createClinic.telephone1')" data-test="inputClinicPhone1" />
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="telephone1">{{ $t('createClinic.telephone1') }}*</label>
            </div>
          </section>

          <section id="localization">
            <h3 class="sub-title" ref="targets">{{ $t('createClinic.location') }}</h3>
            <div class="form-floating">
              <vee-field name="postalCode" v-model="clinic.postalCode" rules="required" v-slot="{ field, errorMessage }">
                <input v-bind="field" type="text" class="form-control" ref="postalCode" id="postalCode" :placeholder="$t('createClinic.postalCode')" data-test="inputClinicPostalCode"/>
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="address">{{ $t('createClinic.postalCode') }}*</label>
            </div>
            <div class="form-floating">
              <vee-field name="address" v-model="clinic.address" rules="required" v-slot="{ field, errorMessage }">
                <input v-bind="field" type="text" class="form-control" ref="address" id="address" :placeholder="$t('createClinic.address')" data-test="inputClinicAddress"/>
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="address">{{ $t('createClinic.address') }}</label>
            </div>
            <div class="form-floating">
              <vee-field name="number" v-model="clinic.addressNumber" rules="required" v-slot="{ field, errorMessage }">
                <input v-bind="field" type="text" class="form-control" ref="number" id="number" :placeholder="$t('createClinic.number')" data-test="inputClinicNumber"/>
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="number">{{ $t('createClinic.number') }}*</label>
            </div>
            <div class="form-floating">
              <vee-field name="district" v-model="clinic.district" rules="required" v-slot="{ field, errorMessage }">
                <input v-bind="field" type="text" class="form-control" ref="district" id="district" :placeholder="$t('createClinic.district')" data-test="inputClinicDistrict"/>
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="district">{{ $t('createClinic.district') }}*</label>
            </div>
            <div class="form-floating">
              <vee-field name="city" v-model="clinic.city" rules="required" v-slot="{ field, errorMessage }">
                <input v-bind="field" type="text" class="form-control" ref="city" id="city" :placeholder="$t('createClinic.city')" data-test="inputClinicCity"/>
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="city">{{ $t('createClinic.city') }}</label>
            </div>
            <div class="form-floating">
              <vee-field name="state" v-model="clinic.state" rules="required" v-slot="{ field, errorMessage }">
                <input v-bind="field" type="text" class="form-control" ref="state" id="state" :placeholder="$t('createClinic.state')" data-test="inputClinicState"/>
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="state">{{ $t('createClinic.state') }}</label>
            </div>
            <div class="form-floating v-select-floating">
              <vee-field name="country" rules="required" v-slot="{ field, errorMessage }">
                <v-select
                  v-bind="field"
                  class="v-select form"
                  v-model="countrySelected"
                  label="label"
                  :reduce="x => x.value"
                  :options="countryOptions"
                  :placeholder="$t('createClinic.country')"
                  data-test="vSelectCountry">
                  <template #search="{attributes, events}">
                    <input class="vs__search" :required="!countrySelected" v-bind="attributes" v-on="events"/>
                  </template>
                </v-select>
                <span class="vee-error">{{ errorMessage }}</span>
              </vee-field>
              <label for="countrySelected">{{ $t('createClinic.country') }}*</label>
            </div>
          </section>

        </form>
      </vee-form>
    </div>

    <div class="offcanvas-footer">
      <button type="submit" class="btn btn-success" form="clinicDataForm" data-test="btnConfirm">{{ $t('save') }}</button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.offcanvas {
  .offcanvas-header,
  .offcanvas-body {
    padding:                            0;
  }

  .offcanvas-header {
    display:                            block;
  }

  .offcanvas-title {
    color:                              var(--Brand-Primary);
    font-size:                          38px;
    font-weight:                        300;
    line-height:                        normal;
    margin:                             26px 0;
  }

  .offcanvas-body {
    padding-right:                      10px;

    section {
      display:                          flex;
      flex-direction:                   column;
      gap:                              19px;
      margin-bottom:                    26px;
    }

    .vee-error {
      right:                            unset;
      left:                             0;
    }

    :deep(ul.vs__dropdown-menu) {
      max-height:                       400px !important;
    }
  }

  .offcanvas-footer {
    display:                            flex;
    flex-direction:                     column;
    gap:                                26px;

    .btn {
      font-size:                        var(--font-size-14);
      font-weight:                      700;
      line-height:                      normal;
      letter-spacing:                   4.2px;
      text-align:                       center;
      text-transform:                   uppercase;
      width:                            100%;
      padding:                          20px;
    }

    .btn-success {
      color:                            var(--Shades-W);
    }
  }
}
</style>

<script>
export default {
  name: 'PlansClinicDataOffcanvas',
  props: {
    offcanvas_id: String
  },
  data: () => ({
    offcanvas_clinic_data: null,
    clinic: null,
    countryOptions: [],
    countrySelected: '',
    setFieldValue: null,
    setFieldTouched: null,
  }),
  watch: {
    countrySelected(newVal) {
      if (this.setFieldValue) {
        this.setFieldValue('country', newVal);
        if (newVal) this.setFieldTouched('country', true);
      }
    },
  },
  methods: {
    async show(options = {}) {
      this.clinic = null
      this.countrySelected = ''
      this.countryOptions = []

      this.clinic = JSON.parse(JSON.stringify(options.clinic));

      await this.setLocaleConfig()

      this.$nextTick(() => {
        this.setVeeValidateFields();
      });

      this.offcanvas_clinic_data = new bootstrap.Offcanvas(document.getElementById(this.offcanvas_id)).show()

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },

    doConfirm() {
      this.clearOffcanvas()
      this.offcanvas_clinic_data = null;
      this.resolvePromise(true)
    },

    doCancel() {
      this.clearOffcanvas()
      this.offcanvas_clinic_data = null;
      this.resolvePromise(false)
    },

    clearOffcanvas() {
      const element = document.getElementById(this.offcanvas_id);
      const instance = bootstrap.Offcanvas.getInstance(element);
      if (instance)
        instance.hide();

      this.clinic = null
      this.countrySelected = ''
    },

    async doSave() {
      await this.$helpers.requestHandling(async () => {
        if (!this.clinic)
          return

        let params = {
          clinic: {
            ...this.clinic,
            country: this.countrySelected,
            email: this.clinic.email.toLowerCase()
          }
        }

        if (this.clinic.parent)
          params.clinic.parent = this.clinic.parent.id

        params.clinic.id = this.clinic.id

        let response = await this.$clinicService.updateClinic(params)
        this.clinic = response.clinic

        this.$nextTick(() => {
          this.doConfirm();
        })
      }, this);
    },

    async setLocaleConfig() {
      let locale = this.clinic.locale
      this.countryOptions = []

      const countryCodeList = await this.$utils.countryCodeList(locale)

      if (locale) {
        for (let code of countryCodeList) {
          this.countryOptions.push({
            value: code,
            label: this.$i18n.t(`country.${code}`)
          })
        }
      }

      const initialCountryValue = this.clinic.country;
      const foundOption = this.countryOptions.find(opt => opt.value === initialCountryValue);
      this.countrySelected = foundOption ? initialCountryValue : null

      return Promise.resolve();
    },

    setVeeValidateFields() {
      if (!this.setFieldValue) return;

      const fields = {
        country: this.countrySelected,
        name: this.clinic.name,
        address: this.clinic.address,
        city: this.clinic.city,
        state: this.clinic.state,
        postalCode: this.clinic.postalCode,
        document: this.clinic.document,
        ownerDocument: this.clinic.ownerDocument,
        district: this.clinic.district,
        number: this.clinic.addressNumber,
        telephone1: this.clinic.telephone1
      };

      Object.entries(fields).forEach(([name, value]) => {
        if (value) {
          this.setFieldValue(name, value);
          this.setFieldTouched(name, true);
        }
      });
    },

    captureProviderFunctions(setFieldValue, setFieldTouched) {
      this.setFieldValue = setFieldValue;
      this.setFieldTouched = setFieldTouched;
      return true;
    },
  }
}
</script>
